import React, { useState, useEffect } from 'react';
import './PayrollTable.css';

const API = '/api/payroll';

const STATUS_LABELS = { draft: 'Черновик', submitted: 'На проверке', approved: 'Утверждено', paid: 'Выплачено' };
const STATUS_COLORS = { draft: '#6b7280', submitted: '#d97706', approved: '#059669', paid: '#2563eb' };

export default function Payroll() {
  const [periods, setPeriods]     = useState([]);
  const [objects, setObjects]     = useState({ hotels: [], departments: [] });
  const [lines, setLines]         = useState([]);
  const [selected, setSelected]   = useState(null);
  const [filterHotel, setFilter]  = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [periodDates, setPeriodDates]   = useState([]);
  const [newPeriod, setNewPeriod] = useState({ object_type: 'hotel', object_id: '', date_from: '', date_to: '' });
  const [showNew, setShowNew]     = useState(false);
  const [calculating, setCalc]    = useState(false);
  const [editPeriod, setEditPeriod]     = useState(null);
  const [editOfficial, setEditOfficial] = useState({}); // { employee_id: value }
  const [savingOfficial, setSaving]     = useState({});
  const [showUpload, setShowUpload]   = useState(false);
  const [uploadData, setUploadData]   = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [applying, setApplying]       = useState(false);
  const [editDates, setEditDates]   = useState({ date_from: '', date_to: '' });

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadPeriods(); }, [filterHotel]);
  useEffect(() => {
    fetch(`${API}/periods/dates`).then(r => r.json()).then(d => setPeriodDates(Array.isArray(d) ? d : []));
  }, []);

  async function loadRefs() {
    const [objs, periods] = await Promise.all([
      fetch(`${API}/hotels-and-depts`).then(r => r.json()),
      fetch(`${API}/periods`).then(r => r.json()),
    ]);
    setObjects(objs); setPeriods(periods);
  }

  async function loadPeriods() {
    let url = `${API}/periods?`;
    if (filterHotel) url += `hotel_id=${filterHotel}`;
    const data = await fetch(url).then(r => r.json());
    setPeriods(data);
  }

  async function uploadFile(file) {
    if (!selected) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API}/accountant/upload?period_id=${selected.id}`, {
      method: 'POST',
      body: formData,
    });
    const data = await res.json();
    setUploadData(data.parsed || []);
    setUploading(false);
  }

  async function applyAdvances() {
    if (!uploadData || !selected) return;
    setApplying(true);
    const toApply = uploadData.filter(d => d.matched_id && d.apply !== false);
    await fetch(`${API}/accountant/apply?period_id=${selected.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(toApply),
    });
    setShowUpload(false);
    setUploadData(null);
    setApplying(false);
    calculate();
  }

  async function saveOfficial(employeeId, value) {
    if (!selected) return;
    setSaving(s => ({ ...s, [employeeId]: true }));
    await fetch(`${API}/payroll/${selected.id}/official`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employee_id: employeeId, amount: parseFloat(value) || 0 }),
    });
    setSaving(s => ({ ...s, [employeeId]: false }));
    setEditOfficial(e => { const n = {...e}; delete n[employeeId]; return n; });
    // Обновляем данные
    const data = await fetch(`${API}/payroll/${selected.id}`).then(r => r.json());
    setLines(Array.isArray(data) ? data : []);
  }

  async function deletePeriod(periodId) {
    if (!window.confirm('Удалить период и все его данные?')) return;
    await fetch(`${API}/periods/${periodId}`, { method: 'DELETE' });
    setSelected(null); setLines([]);
    loadPeriods();
  }

  async function savePeriodDates() {
    await fetch(`${API}/periods/${editPeriod.id}/dates?date_from=${editDates.date_from}&date_to=${editDates.date_to}`, { method: 'PATCH' });
    setEditPeriod(null);
    loadPeriods();
  }

  async function createPeriod() {
    const body = {
      hotel_id: newPeriod.object_type === 'hotel' ? parseInt(newPeriod.object_id) : null,
      date_from: newPeriod.date_from,
      date_to: newPeriod.date_to,
    };
    await fetch(`${API}/periods`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setShowNew(false);
    setNewPeriod({ object_type: 'hotel', object_id: '', date_from: '', date_to: '' });
    loadPeriods();
  }

  async function selectPeriod(p) {
    setSelected(p);
    setLines([]);
    const data = await fetch(`${API}/payroll/${p.id}`).then(r => r.json());
    setLines(Array.isArray(data) ? data : []);
  }

  async function calculate() {
    if (!selected) return;
    setCalc(true);
    setLines([]);
    await fetch(`${API}/payroll/${selected.id}/calculate`, { method: 'POST' });
    const data = await fetch(`${API}/payroll/${selected.id}`).then(r => r.json());
    setLines(Array.isArray(data) ? data : []); 
    setCalc(false);
  }

  async function changeStatus(status) {
    if (!selected) return;
    await fetch(`${API}/periods/${selected.id}/status?status=${status}`, { method: 'PATCH' });
    loadPeriods();
    setSelected(p => ({ ...p, status }));
  }

  const fmt = v => v ? parseFloat(v).toLocaleString('ru') + ' ₽' : '—';

  const totals = lines.reduce((acc, l) => {
    acc.accrued  += parseFloat(l.accrued  || 0);
    acc.fines    += parseFloat(l.fines    || 0);
    acc.bonuses  += parseFloat(l.bonuses  || 0);
    acc.calls    += parseFloat(l.calls    || 0);
    acc.advance  += parseFloat(l.advance  || 0);
    acc.official += parseFloat(l.official || 0);
    acc.total    += parseFloat(l.total    || 0);
    acc.shifts   += parseFloat(l.shifts_count || 0);
    return acc;
  }, { accrued: 0, fines: 0, bonuses: 0, calls: 0, advance: 0, official: 0, total: 0, shifts: 0 });

  const GROUPS = [
    ['manager', 'Управляющие'], ['spir', 'СПиР'], ['admin', 'Администраторы'],
    ['maid', 'Горничные'], ['tech', 'Техники'],
  ];
  const groupedRows = (() => {
    const known = GROUPS.map(g => g[0]);
    const buckets = GROUPS.map(([key, title]) => ({ title, items: lines.filter(l => l.category === key) }));
    buckets.push({ title: 'Остальные', items: lines.filter(l => !known.includes(l.category)) });
    const rows = [];
    buckets.forEach(b => {
      if (!b.items.length) return;
      rows.push({ type: 'head', title: b.title, count: b.items.length });
      b.items.forEach(l => rows.push({ type: 'line', line: l }));
      const sum = k => b.items.reduce((a, l) => a + parseFloat(l[k] || 0), 0);
      rows.push({ type: 'sub', title: b.title, shifts: sum('shifts_count'), accrued: sum('accrued'), fines: sum('fines'),
                  bonuses: sum('bonuses'), calls: sum('calls'), advance: sum('advance'), official: sum('official'), total: sum('total') });
    });
    return rows;
  })();

  const filteredPeriods = periods.filter(p => {
    if (filterPeriod) {
      const [df, dt] = filterPeriod.split('|');
      return p.date_from === df && p.date_to === dt;
    }
    return true;
  });

  return (
    <div className="pt-page">
      <div className="pt-header">
        <h2>Ведомость</h2>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ Новый период</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'320px 1fr', gap:24 }}>
        <div>
          <div style={{ marginBottom:12, display:'flex', flexDirection:'column', gap:8 }}>
            <select value={filterPeriod} onChange={e => {
              setFilterPeriod(e.target.value);
              setSelected(null); setLines([]);
            }} style={{ width:'100%', padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14 }}>
              <option value="">— все периоды —</option>
              {periodDates.map((p, i) => (
                <option key={i} value={`${p.date_from}|${p.date_to}`}>
                  {new Date(p.date_from).toLocaleDateString('ru')} – {new Date(p.date_to).toLocaleDateString('ru')}
                </option>
              ))}
            </select>
            <select value={filterHotel} onChange={e => { setFilter(e.target.value); setSelected(null); setLines([]); }} style={{ width:'100%', padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14 }}>
              <option value="">Все объекты</option>
              <optgroup label="Отели">
                {objects.hotels?.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </optgroup>
            </select>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {filteredPeriods.length === 0 && <div style={{ color:'#9ca3af', fontSize:14, padding:16 }}>Периодов нет</div>}
            {filteredPeriods.map(p => (
              <div
                key={p.id}
                onClick={() => selectPeriod(p)}
                style={{
                  padding:'12px 16px', borderRadius:10, cursor:'pointer', background:'#fff',
                  border: selected?.id === p.id ? '2px solid #111827' : '2px solid transparent',
                  boxShadow:'0 1px 3px rgba(0,0,0,0.08)',
                }}
              >
                <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>{p.hotel_name}</div>
                <div style={{ fontSize:12, color:'#6b7280' }}>
                  {new Date(p.date_from).toLocaleDateString('ru')} – {new Date(p.date_to).toLocaleDateString('ru')}
                </div>
                <div style={{ marginTop:6, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:11, fontWeight:600, color: STATUS_COLORS[p.status], background: STATUS_COLORS[p.status] + '20', padding:'2px 8px', borderRadius:20 }}>
                    {STATUS_LABELS[p.status]}
                  </span>
                  <div style={{ display:'flex', gap:4 }} onClick={e => e.stopPropagation()}>
                    <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, padding:'2px 6px', borderRadius:4, color:'#6b7280' }}
                      onClick={() => { setEditPeriod(p); setEditDates({ date_from: p.date_from, date_to: p.date_to }); }}>✏️</button>
                    <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, padding:'2px 6px', borderRadius:4, color:'#dc2626' }}
                      onClick={() => deletePeriod(p.id)}>✕</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          {!selected ? (
            <div style={{ textAlign:'center', color:'#9ca3af', padding:60, background:'#fff', borderRadius:12 }}>
              Выберите период слева
            </div>
          ) : (
            <>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:16 }}>{selected.hotel_name}</div>
                  <div style={{ fontSize:13, color:'#6b7280' }}>
                    {new Date(selected.date_from).toLocaleDateString('ru')} – {new Date(selected.date_to).toLocaleDateString('ru')}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn-secondary" onClick={calculate} disabled={calculating}>
                    {calculating ? 'Считаем...' : '⟳ Пересчитать'}
                  </button>
                  <button className="btn-secondary" onClick={() => {
                    window.open(`/api/payroll/export/schedule?period_id=${selected.id}`, '_blank');
                  }}>
                    📥 Выгрузить табель
                  </button>

                  {selected.status === 'draft'     && <button className="btn-secondary" onClick={() => changeStatus('submitted')}>На проверку</button>}
                  {selected.status === 'submitted' && <button className="btn-primary"   onClick={() => changeStatus('approved')}>Утвердить</button>}
                  {selected.status === 'approved'  && <button className="btn-primary"   onClick={() => changeStatus('paid')}>Выплачено</button>}
                </div>
              </div>

              <div className="stat-cards" style={{ gridTemplateColumns:'repeat(5,1fr)', marginBottom:16 }}>
                <div className="stat-card"><div className="label">Смен</div><div className="value" style={{ fontSize:20 }}>{totals.shifts}</div></div>
                <div className="stat-card"><div className="label">Начислено</div><div className="value" style={{ fontSize:18 }}>{totals.accrued.toLocaleString()} ₽</div></div>
                <div className="stat-card"><div className="label">Штрафы</div><div className="value red" style={{ fontSize:18 }}>−{totals.fines.toLocaleString()} ₽</div></div>
                <div className="stat-card"><div className="label">Плюсы</div><div className="value green" style={{ fontSize:18 }}>+{totals.bonuses.toLocaleString()} ₽</div></div>
                <div className="stat-card"><div className="label">К выплате</div><div className="value" style={{ fontSize:18 }}>{totals.total.toLocaleString()} ₽</div></div>
              </div>

              <table className="pt-table">
                <thead>
                  <tr>
                    <th>Сотрудник</th><th>Должность</th><th>Смен</th>
                    <th>Начислено</th><th>Штрафы</th><th>Плюсы</th><th>Звонки</th><th>Авансы</th><th>Оф. часть</th><th>Итого</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={10} style={{ textAlign:'center', color:'#9ca3af', padding:40 }}>
                      Нажмите «Пересчитать» для формирования ведомости
                    </td></tr>
                  )}
                  {groupedRows.map((row, i) => {
                    if (row.type === 'head') return (
                      <tr key={'h' + i}>
                        <td colSpan={10} style={{ background:'#f3f4f6', fontWeight:700, fontSize:12, letterSpacing:0.5, textTransform:'uppercase', color:'#374151' }}>
                          {row.title} ({row.count})
                        </td>
                      </tr>
                    );
                    if (row.type === 'sub') return (
                      <tr key={'s' + i} style={{ background:'#fafafa', fontWeight:600 }}>
                        <td colSpan={2} style={{ textAlign:'right', color:'#6b7280' }}>Итого: {row.title}</td>
                        <td>{row.shifts}</td>
                        <td>{row.accrued.toLocaleString()} ₽</td>
                        <td>{row.fines ? '−' + row.fines.toLocaleString() + ' ₽' : '—'}</td>
                        <td>{row.bonuses ? '+' + row.bonuses.toLocaleString() + ' ₽' : '—'}</td>
                        <td>{row.calls ? row.calls.toLocaleString() + ' ₽' : '—'}</td>
                        <td>{row.advance ? '−' + row.advance.toLocaleString() + ' ₽' : '—'}</td>
                        <td>{row.official ? '−' + row.official.toLocaleString() + ' ₽' : '—'}</td>
                        <td>{row.total.toLocaleString()} ₽</td>
                      </tr>
                    );
                    const l = row.line;
                    return (
                    <tr key={i}>
                      <td><strong>{l.full_name}</strong></td>
                      <td style={{ color:'#6b7280', fontSize:13 }}>{l.position || l.category}</td>
                      <td>{l.shifts_count}</td>
                      <td>{fmt(l.accrued)}</td>
                      <td style={{ color: parseFloat(l.fines) > 0 ? '#dc2626' : undefined }}>
                        {parseFloat(l.fines) > 0 ? `−${parseFloat(l.fines).toLocaleString()} ₽` : '—'}
                      </td>
                      <td style={{ color: parseFloat(l.bonuses) > 0 ? '#059669' : undefined }}>
                        {parseFloat(l.bonuses) > 0 ? `+${parseFloat(l.bonuses).toLocaleString()} ₽` : '—'}
                      </td>
                      <td style={{ color: parseFloat(l.calls) !== 0 ? (parseFloat(l.calls) > 0 ? '#059669' : '#dc2626') : undefined }}>
                        {parseFloat(l.calls) !== 0 ? `${parseFloat(l.calls) > 0 ? '+' : ''}${parseFloat(l.calls).toLocaleString()} ₽` : '—'}
                      </td>
                      <td style={{ color: parseFloat(l.advance) > 0 ? '#dc2626' : undefined }}>
                        {parseFloat(l.advance) > 0 ? `−${parseFloat(l.advance).toLocaleString()} ₽` : '—'}
                      </td>
                      <td>
                        {editOfficial[l.employee_id] !== undefined ? (
                          <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                            <input
                              type="number"
                              min="0"
                              value={editOfficial[l.employee_id]}
                              onChange={e => setEditOfficial(o => ({ ...o, [l.employee_id]: e.target.value }))}
                              style={{ width:90, padding:'3px 6px', border:'1px solid #3b82f6', borderRadius:6, fontSize:13 }}
                              autoFocus
                            />
                            <button
                              style={{ background:'#3b82f6', color:'#fff', border:'none', borderRadius:6, padding:'3px 8px', cursor:'pointer', fontSize:12 }}
                              onClick={() => saveOfficial(l.employee_id, editOfficial[l.employee_id])}
                              disabled={savingOfficial[l.employee_id]}
                            >✓</button>
                            <button
                              style={{ background:'#f3f4f6', border:'none', borderRadius:6, padding:'3px 6px', cursor:'pointer', fontSize:12 }}
                              onClick={() => setEditOfficial(o => { const n={...o}; delete n[l.employee_id]; return n; })}
                            >✕</button>
                          </div>
                        ) : (
                          <span
                            style={{ color: parseFloat(l.official) > 0 ? '#9333ea' : '#9ca3af', cursor:'pointer' }}
                            onClick={() => setEditOfficial(o => ({ ...o, [l.employee_id]: parseFloat(l.official) || 0 }))}
                            title="Нажмите для редактирования"
                          >
                            {parseFloat(l.official) > 0 ? `−${parseFloat(l.official).toLocaleString()} ₽` : '—'}
                          </span>
                        )}
                      </td>
                      <td><strong style={{ color: parseFloat(l.total) >= 0 ? '#111827' : '#dc2626' }}>{fmt(l.total)}</strong></td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {showNew && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Новый расчётный период</h3>
              <button className="btn-icon" onClick={() => setShowNew(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>Тип подразделения *</label>
                <select value={newPeriod.object_type} onChange={e => setNewPeriod(p => ({ ...p, object_type: e.target.value, object_id: '' }))}>
                  <option value="hotel">Отель</option>
                  <option value="dept">Подразделение (Офис / ОТА / ОБ)</option>
                </select>
              </div>
              <div className="form-row">
                <label>{newPeriod.object_type === 'hotel' ? 'Отель *' : 'Подразделение *'}</label>
                <select value={newPeriod.object_id} onChange={e => setNewPeriod(p => ({ ...p, object_id: e.target.value }))}>
                  <option value="">— выберите —</option>
                  {newPeriod.object_type === 'hotel'
                    ? objects.hotels?.map(h => <option key={h.id} value={h.id}>{h.name}</option>)
                    : objects.departments?.map(d => <option key={d.id} value={d.id}>{d.name}</option>)
                  }
                </select>
              </div>
              <div className="form-grid">
                <div className="form-row">
                  <label>Дата начала *</label>
                  <input type="date" value={newPeriod.date_from} onChange={e => setNewPeriod(p => ({ ...p, date_from: e.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Дата конца *</label>
                  <input type="date" value={newPeriod.date_to} onChange={e => setNewPeriod(p => ({ ...p, date_to: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowNew(false)}>Отмена</button>
              <button className="btn-primary" onClick={createPeriod} disabled={!newPeriod.object_id || !newPeriod.date_from || !newPeriod.date_to}>
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpload && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3>Загрузка файла бухгалтера</h3>
              <button className="btn-icon" onClick={() => { setShowUpload(false); setUploadData(null); }}>✕</button>
            </div>
            <div className="modal-body">
              {!uploadData ? (
                <div>
                  <div style={{ marginBottom:16, fontSize:13, color:'#6b7280' }}>
                    Загрузите Excel файл от бухгалтера. ИИ автоматически найдёт суммы и сопоставит с сотрудниками.
                  </div>
                  <div style={{
                    border:'2px dashed #e5e7eb', borderRadius:12, padding:40,
                    textAlign:'center', cursor:'pointer',
                    background: uploading ? '#f9fafb' : '#fff'
                  }}
                    onClick={() => document.getElementById('accountant-file').click()}
                  >
                    {uploading ? (
                      <div style={{ color:'#6b7280' }}>⏳ ИИ парсит файл...</div>
                    ) : (
                      <div>
                        <div style={{ fontSize:32, marginBottom:8 }}>📊</div>
                        <div style={{ color:'#374151', fontWeight:500 }}>Нажмите для выбора файла</div>
                        <div style={{ color:'#9ca3af', fontSize:12, marginTop:4 }}>Excel (.xlsx)</div>
                      </div>
                    )}
                  </div>
                  <input
                    id="accountant-file"
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display:'none' }}
                    onChange={e => e.target.files[0] && uploadFile(e.target.files[0])}
                  />
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom:12, fontSize:13, color:'#6b7280' }}>
                    Найдено записей: {uploadData.length} · Сопоставлено: {uploadData.filter(d => d.matched_id).length}
                  </div>
                  <div style={{ maxHeight:400, overflowY:'auto' }}>
                    <table className="pt-table">
                      <thead>
                        <tr>
                          <th>В файле</th><th>Сумма</th><th>Сопоставлен с</th><th>✓</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadData.map((d, i) => (
                          <tr key={i} style={{ opacity: d.apply === false ? 0.4 : 1 }}>
                            <td style={{ fontSize:13, color:'#374151' }}>{d.file_name}</td>
                            <td style={{ fontWeight:600, color:'#dc2626' }}>−{parseFloat(d.amount).toLocaleString()} ₽</td>
                            <td style={{ fontSize:13, color: d.matched_id ? '#059669' : '#dc2626' }}>
                              {d.matched_name || '❌ не найден'}
                            </td>
                            <td>
                              <input type="checkbox"
                                checked={d.apply !== false && !!d.matched_id}
                                disabled={!d.matched_id}
                                onChange={e => {
                                  const newData = [...uploadData];
                                  newData[i] = { ...newData[i], apply: e.target.checked };
                                  setUploadData(newData);
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowUpload(false); setUploadData(null); }}>Отмена</button>
              {uploadData && (
                <button className="btn-primary" onClick={applyAdvances} disabled={applying}>
                  {applying ? 'Применяем...' : `Применить авансы (${uploadData.filter(d => d.matched_id && d.apply !== false).length})`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {editPeriod && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:400 }}>
            <div className="modal-header">
              <h3>Редактировать период</h3>
              <button className="btn-icon" onClick={() => setEditPeriod(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>Дата начала</label>
                <input type="date" value={editDates.date_from} onChange={e => setEditDates(d => ({ ...d, date_from: e.target.value }))} />
              </div>
              <div className="form-row">
                <label>Дата конца</label>
                <input type="date" value={editDates.date_to} onChange={e => setEditDates(d => ({ ...d, date_to: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setEditPeriod(null)}>Отмена</button>
              <button className="btn-primary" onClick={savePeriodDates}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
